<template>
  <slot name="header_close" />
  <div class="wrapper">
    <h3 class="title">
      {{ $t("dialog.settings-number.title") }}
      <div class="number-block">
        <img class="icon" :src="require(`@/assets/icons/${messenger}.svg`).default" />
        <span class="mt-1">{{ number }}</span>
      </div>
    </h3>
    <!-- VISIBILITY -->
    <div class="content mt-4">
      <div class="setting_block">
        <h4 class="block_title">
          {{ $t("dialog.settings-number.blocks.hide_number") }}
        </h4>
        <div class="action_container">
          <Toggle v-if="!isLoading" v-model="isHide" @click="hideNumber" />
          <span v-else class="spinner"></span>
        </div>
      </div>
      <!-- APIKEY -->
      <div class="setting_block">
        <h4 class="block_title">
          {{ $t("dialog.settings-number.blocks.api_key") }}
        </h4>
        <div class="action_container">
          <button class="inside_block text" @click="updateApiKey">
            <RefreshApi class="icon" />
            {{ $t("common.update") }}
          </button>
        </div>
      </div>
      <!-- TAGS -->
      <div class="setting_block wide_block">
        <div class="block_title">
          <h4>
            {{ $t("tags.tag_number") }}
          </h4>
          <BaseTag :tag="tagList[0]" typeClass="table" />
        </div>
        <div class="action_container" :class="{ multi_action: !!tagList.length }">
          <button
            v-if="!tagList.length"
            class="inside_block text"
            @click="actionTagMenu.add"
          >
            <i class="bi bi-plus-lg"></i>
            {{ $t("common.add") }}
            <!-- <b-icon-tags-fill class="icon" /> -->
          </button>
          <template v-else>
            <button
              class="inside_block"
              :title="$t('tags.edit_tag')"
              @click="actionTagMenu.edit"
            >
              <i class="icon fa fa-pencil"></i>
              <!-- <b-icon-pencil-fill class="icon" /> -->
            </button>
            <button
              class="inside_block"
              :title="$t('tags.delete_tag')"
              @click="actionTagMenu.delete"
            >
              <i class="icon fa fa-trash"></i>
              <!-- <b-icon-trash3-fill class="icon" /> -->
            </button>
          </template>
        </div>
      </div>
      <!-- WEBHOOKS -->
      <div
        class="setting_block wide_block"
        :style="!!webhook.length && { height: '60px' }"
      >
        <div class="block_title">
          <h4>Webhook URL</h4>
        </div>
        <ul class="information" v-show="!!webhook.length">
          <li class="info_item" :title="`Incoming/Outgoing WEBHOOK URL: ${webhook[0]}`">
            <b class="item_title"> In/Out </b>
            <i class="text_overflow">{{ webhook[0] }}</i>
          </li>
          <li class="info_item" :title="`Status WEBHOOK URL: ${webhook[1]}`">
            <b class="item_title">Status </b>
            <i class="text_overflow">{{ webhook[1] }}</i>
          </li>
        </ul>
        <div class="action_container multi_action">
          <button
            v-if="!webhook.length"
            class="inside_block text"
            @click="actionWebhookMenu.add"
          >
            <i class="bi bi-plus-lg"></i>
            {{ $t("common.add") }}
            <!-- <b-icon-tags-fill class="icon" /> -->
          </button>
          <template v-else>
            <button
              class="inside_block"
              :title="$t('common.edit')"
              @click="actionWebhookMenu.edit"
            >
              <i class="icon fa fa-pencil"></i>
              <!-- <b-icon-pencil-fill class="icon" /> -->
            </button>
            <button
              class="inside_block"
              :title="$t('common.delete')"
              @click="actionWebhookMenu.delete"
            >
              <i class="icon fa fa-trash"></i>
              <!-- <b-icon-trash3-fill class="icon" /> -->
            </button>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { useStore } from "vuex";
import Toggle from "@vueform/toggle";
import BaseTag from "@/components/ui/BaseTag.vue";
import RefreshApi from "@/components/Telephony/icons/RefreshApi.vue";
import BaseInput from "@/components/ui/BaseInput.vue";

const store = useStore();

const isHide = ref(null);
const inputValue = ref(null);
const isLoading = ref(false);

const number = computed(() => store.state.dialogParams?.info?.number);
const messenger = computed(() => store.state.dialogParams?.info?.messenger);
const visibility = computed(() => store.state.dialogParams?.info?.visibility);
const tagList = computed(() => {
  const tagList = store.state.dialogParams?.info?.tags || [];
  return tagList;
});
const webhook = computed(() => {
  let arr = [];
  store.state.dialogParams?.info?.webhooks.forEach((el) => {
    el.type === "inbox" ? (arr[0] = el.url) : (arr[1] = el.url);
  });
  return arr;
});

watch(
  visibility,
  () => {
    isHide.value = !visibility.value;
  },
  { immediate: true }
);

const hideNumber = () => {
  isLoading.value = true;
  const msgr = messenger.value;
  store
    .dispatch("numbers/updateVisibility", {
      number: number.value,
      visibility: !isHide.value,
      messenger: messenger.value,
    })
    .then(() => {
      store.commit("TOGGLE_HIDE_NUMBER", !isHide.value);
      store.dispatch("numbers/loadNumbersByMessenger", {
        messenger: msgr,
        visibility: true,
      });
      store.dispatch("numbers/loadNumbersByMessenger", {
        messenger: msgr,
        visibility: false,
      });
      isLoading.value = false;
    });
};

const updateApiKey = async () => {
  store.commit("setNextDialog", "SettingsNumberDialog");
  store.commit("setDialog", "ApiKeyRefreshDialog");
};

const actionTagMenu = {
  add: () => {
    store.commit("setNextDialog", "SettingsNumberDialog");
    store.commit("setDialog", "NumberTagsAddEditDialog");
  },
  edit: () => {
    store.commit("setNextDialog", "SettingsNumberDialog");
    store.commit("setDialog", "NumberTagsAddEditDialog");
  },
  delete: () => {
    store.commit("setNextDialog", "SettingsNumberDialog");
    store.commit("setDialog", "NumberTagsDeleteDialog");
  },
};

const actionWebhookMenu = {
  add: () => {
    store.commit("setTypeOfDialogAction", "add");
    store.commit("setNextDialog", "SettingsNumberDialog");
    store.commit("setDialog", "WebhookAddEditDialog");
  },
  edit: () => {
    store.commit("setTypeOfDialogAction", "edit");
    store.commit("setNextDialog", "SettingsNumberDialog");
    store.commit("setDialog", "WebhookAddEditDialog");
  },
  delete: () => {
    store.commit("setTypeOfDialogAction", "delete");
    store.commit("setNextDialog", "SettingsNumberDialog");
    store.commit("setDialog", "WebhookDeleteDialog");
  },
};
</script>

<style lang="scss">
@use "@/styles/toggle.scss";
</style>

<style lang="scss" scoped>
.wrapper {
  padding: 20px 28px 42px;
  max-width: 750px;
  width: 100%;
  box-sizing: border-box;
  border-radius: 7px;
  background-color: white;
  text-align: center;
}

.title {
  font-size: 20px;
  text-align: center;
  font-family: Gilroy;
  font-weight: 500;
  text-transform: uppercase;
  color: var(--text-color);
}

.icon {
  width: 20px;
  height: 20px;
  font-size: 18px;
}

.number-block {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 10px;
  font-size: 16px;
  font-weight: 500;
  color: var(--text-color);
}

.content {
  display: flex;
  gap: 10px;
  justify-content: center;
  // justify-content: space-between;
  flex-wrap: wrap;
}

.action_container {
  display: flex;
  justify-content: center;
}
// the spinner in place of the toggle, as in dialog-form.scss
.action_container .spinner {
  width: 1.4em;
  height: 1.4em;
  margin: 0;
}
.multi_action {
  width: fit-content;
  gap: 10px;
}

.setting_block {
  height: 50px;
  min-width: 240px;
  width: calc(50% - 5px);
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  padding: 10px;
  border-radius: 7px;
  margin-bottom: 20px;
  border: 1px solid var(--general-color);
  background-color: var(--block-color);
  transition: all 0.7s ease;
  &:hover {
    background-color: var(--block-second-color);
    // transform: translate(1px, 1px);
  }
}

.wide_block {
  width: 100%;
  justify-content: space-between;
  .block_title {
    display: flex;
    justify-content: center;
    align-items: center;
    flex: none;
    gap: 10px;
    width: fit-content;
  }
}

.setting_block:not(.wide_block) .block_title {
  white-space: nowrap;
}

.block_title {
  flex: 1;
  text-align: start;
  font-family: "Gilroy";
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  text-transform: uppercase;
  line-height: 24px;
  color: rgba(0, 0, 0, 0.6);
}

.information {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-left: 14px;
  line-height: 19px;
  font-size: 14px;
  overflow: hidden;
  //   border-left: 2px dashed var(--border-light-color);
  //   border-right: 2px dashed var(--border-light-color);
  //   margin: 0 -3px 0 -5px;
  //   padding-left: 5px;
}

.info_item {
  cursor: pointer;
  display: flex;
  gap: 6px;
  width: 100%;
  min-width: 0;
  color: var(--text-third-color);
  align-items: center;
}

// the url scrolls behind a fade, as the password in the sip cards
.text_overflow {
  flex: 1 1 0;
  min-width: 0;
  overflow-x: auto;
  white-space: nowrap;

  // html sets Figtree, which the project does not ship
  font-family: "Gilroy", sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 19px;
  letter-spacing: 0.05rem;
  color: var(--text-third-color);

  // the scrollbar eats 5px inside the row and lifts the url off the label
  // baseline; wheel and trackpad scrolling still work
  scrollbar-width: none;
  -ms-overflow-style: none;

  -webkit-mask-image: linear-gradient(to right, #000 calc(100% - 24px), transparent 100%);
  mask-image: linear-gradient(to right, #000 calc(100% - 24px), transparent 100%);

  &::-webkit-scrollbar {
    display: none;
  }
}

// the tag reads as a value, its brackets as quiet as the webhook labels
.wide_block :deep(.badge.table) {
  margin-top: 0;
  color: var(--border-primary-color);

  .tags {
    gap: 4px;
  }

  .tag {
    max-width: 220px;
    font-size: 14px;
    font-weight: 500;
    letter-spacing: 0.05rem;
    color: var(--text-third-color);
  }
}

.inside_block {
  position: relative;
  min-width: 35px;
  width: 35px;
  height: 35px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 9px;
  border: 1px solid var(--general-color);
  background-color: #ffffff;

  transition: 0.25s;
  &.text {
    width: 140px;
    min-width: 140px;
    padding: 0 16px;
    gap: 10px;
    color: var(--general-color);
    font-family: "Gilroy", sans-serif;
    font-size: 14px;
    font-weight: 600;
    line-height: 17px;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    white-space: nowrap;

    i {
      font-size: 16px;
    }

    .icon {
      flex: 0 0 auto;
      width: 22px;
      height: 22px;
    }
  }
  .icon {
    fill: var(--general-color);
    color: var(--general-color);
    transition: 0.25s;
  }

  &:hover {
    cursor: pointer;
    background-color: var(--general-color);
    transition: 0.25s;
    &.text {
      color: white;
      transition: 0.25s;
    }
    .icon {
      fill: white;
      color: white;
      transition: 0.25s;
    }
  }
}

.item_title {
  flex: 0 0 auto;
  display: flex;
  font-size: 12px;
  font-weight: 500;
  line-height: 19px;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: var(--border-primary-color);
  &:after {
    content: ":";
  }
}

@media (max-width: 570px) {
  .setting_block {
    width: 100%;
  }
}

@media (max-width: 420px) {
  .information {
    line-height: 22px;
  }
  .info_item {
  }
  .text_overflow {
    display: none;
  }
  .item_title {
    margin: auto;
    color: var(--general-color);
    transition: 0.5s;
    &:after {
      content: "";
    }
    &:hover {
      color: var(--text-light-color);
      transition: 0.5s;
    }
  }
}
</style>
